/**
 * Delay calling `fn` until `wait` ms have passed without another call.
 *
 * Useful for anything triggered by typing — a lookup, a search, a filter — so
 * the work runs once when the user pauses instead of on every keystroke.
 *
 * Defined here rather than imported from lodash: lodash is NOT declared in this
 * project's package.json. It resolves only as a transitive dependency of
 * something else, so `import { debounce } from 'lodash'` works by luck and
 * would break the build if that dependency ever went away.
 *
 * The returned function exposes `.cancel()` to drop a pending call. Cancel on
 * unmount, and whenever a pending call is no longer wanted:
 *
 *   const search = useMemo(() => debounce(doSearch, 400), [doSearch]);
 *   useEffect(() => () => search.cancel(), [search]);
 *
 * In a component, always wrap in `useMemo` (or `useCallback`) — a bare call in
 * the render body creates a new timer every render and never actually debounces.
 *
 * @param {Function} fn   Function to defer.
 * @param {number} wait   Idle time in milliseconds before `fn` runs.
 * @returns {Function}    Debounced function, with a `.cancel()` method.
 */
export const debounce = (fn, wait) => {
  let timer;

  const debounced = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };

  debounced.cancel = () => clearTimeout(timer);

  return debounced;
};
